import React, { useCallback } from 'react';
import { Drawer, Stack } from '@mui/material';
import { useHoldingData } from '@/hooks/useHoldingData';
import { HoldingDetailsFlyoutProps } from '../types';
import { FLYOUT_WIDTH } from '../constants';
import { FlyoutErrorBoundary } from '../components';
import FlyoutLoadingState from '../common/FlyoutLoadingState';
import FlyoutErrorState from '../common/FlyoutErrorState';
import HoldingContent from './HoldingContent';
import HoldingHeader from './HoldingHeader';

const HoldingDetailsFlyout: React.FC<HoldingDetailsFlyoutProps> = ({
  open,
  toggleDrawer,
  holding,
  isColumnEnabled = () => true,
  endDate,
  advisorContext,
  showCusip = false,
  investorId,
}) => {
  // Custom hook for centralized data fetching
  const {
    holdingDetails,
    diversificationData,
    diversificationDetailsData,
    alsoHeldHoldingsData,
    isLoading,
    hasError,
    errors,
  } = useHoldingData({ holding, endDate, investorId });

  // Memoized callback for closing the drawer
  const handleClose = useCallback(() => {
    toggleDrawer(false);
  }, [toggleDrawer]);

  // Early return for no holding
  if (!holding) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <FlyoutLoadingState
        open={open}
        onClose={handleClose}
        message='Loading holding details...'
        dataFieldsCount={9}
        tabsCount={4}
        showProgress={false}
        drawerClassName='holding-details-flyout'
        contentClassName='holding-details-flyout-content'
      />
    );
  }

  // Error state
  if (hasError) {
    return (
      <FlyoutErrorState
        open={open}
        onClose={handleClose}
        error={errors.diversificationError || errors.holdingDetailsError}
        message='Failed to load holding details'
        title='Error Loading Holding'
        subtitle='Holding Details'
      />
    );
  }

  // Main content
  return (
    <FlyoutErrorBoundary>
      <Drawer
        anchor='right'
        open={open}
        onClose={handleClose}
        variant='temporary'
        className='holding-details-flyout'
      >
        <Stack
          sx={{
            width: FLYOUT_WIDTH,
            paddingTop:
              'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
            paddingBottom:
              'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))',
          }}
          className='holding-details-flyout-content'
        >
          <HoldingHeader
            holding={holding}
            onClose={handleClose}
            advisorContext={advisorContext}
          />

          <HoldingContent
            holding={holding}
            holdingDetails={holdingDetails}
            diversificationData={diversificationData}
            diversificationDetailsData={diversificationDetailsData}
            alsoHeldHoldingsData={alsoHeldHoldingsData}
            isColumnEnabled={isColumnEnabled}
            endDate={endDate}
            isDiversificationLoading={false}
            diversificationError={errors.diversificationError}
            showCusip={showCusip}
            investorId={investorId}
          />
        </Stack>
      </Drawer>
    </FlyoutErrorBoundary>
  );
};
export default HoldingDetailsFlyout;
